/**
 * Listing catalog keeps filter/list state in a client reducer and updates
 * permalinks via history.pushState. A Next.js Link SPA navigation changes
 * the URL without running that flow, so the grid intercepts fandom/character
 * clicks through these events and reuses the existing filter handlers.
 */
export const LISTING_FANDOM_NAVIGATE_EVENT = 'listing-fandom-navigate';
export const LISTING_CHARACTER_NAVIGATE_EVENT = 'listing-character-navigate';
/** Catalog root (/fanfics|/originals) from header or breadcrumbs. */
export const LISTING_CATALOG_NAVIGATE_EVENT = 'listing-catalog-navigate';
/** Fandom permalink crumb while still in soft-nav (Next pathname = catalog). */
export const LISTING_FANDOM_HREF_NAVIGATE_EVENT = 'listing-fandom-href-navigate';

interface ListingNavigateHandled {
  /** Set synchronously by FanfictionGrid listener; false = pre-hydration click. */
  handled?: boolean;
}

interface ListingFacetNavigateDetail extends ListingNavigateHandled {
  itemId: string;
  preventNavigation: () => void;
}

interface ListingCharacterNavigateDetail extends ListingNavigateHandled {
  characterId: string;
  fandomId?: string;
  preventNavigation: () => void;
}

interface ListingCatalogNavigateDetail extends ListingNavigateHandled {
  catalogHref: string;
  preventNavigation: () => void;
}

interface ListingFandomHrefNavigateDetail extends ListingNavigateHandled {
  href: string;
  fandomId?: string;
  preventNavigation: () => void;
}

const createListingFacetNavigateHandler = (eventName: string, itemId?: string) => {
  return (event: { preventDefault: () => void }): void => {
    if (!itemId) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent<ListingFacetNavigateDetail>(eventName, {
        detail: {
          itemId,
          preventNavigation: event.preventDefault,
        },
      }),
    );
  };
};

const handleListingFacetNavigateEvent = (event: Event, onChange: (ids: string[]) => void): void => {
  const customEvent = event as CustomEvent<ListingFacetNavigateDetail>;
  const itemId = customEvent.detail?.itemId;
  const preventNavigation = customEvent.detail?.preventNavigation;

  if (!itemId || !preventNavigation) {
    return;
  }

  preventNavigation();
  customEvent.detail.handled = true;
  onChange([itemId]);
};

export const createFandomNavigateHandler = (fandomId?: string) =>
  createListingFacetNavigateHandler(LISTING_FANDOM_NAVIGATE_EVENT, fandomId);

export const createCharacterNavigateHandler = (characterId?: string, fandomId?: string) => {
  return (event: { preventDefault: () => void }): void => {
    if (!characterId) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent<ListingCharacterNavigateDetail>(LISTING_CHARACTER_NAVIGATE_EVENT, {
        detail: {
          characterId,
          fandomId,
          preventNavigation: event.preventDefault,
        },
      }),
    );
  };
};

export const createCatalogNavigateHandler = (catalogHref: '/fanfics' | '/originals') => {
  return (event: { preventDefault: () => void }): void => {
    // Block Next.js soft-nav immediately — grid may not have mounted yet
    // (first click after hard refresh on a permalink page).
    event.preventDefault();

    const detail: ListingCatalogNavigateDetail = {
      catalogHref,
      preventNavigation: () => {},
      handled: false,
    };
    window.dispatchEvent(
      new CustomEvent<ListingCatalogNavigateDetail>(LISTING_CATALOG_NAVIGATE_EVENT, {
        detail,
      }),
    );

    if (detail.handled) {
      return;
    }

    // Grid listener not mounted yet (first click after hard refresh) — full reload.
    window.location.assign(catalogHref);
  };
};

export const handleListingFandomNavigateEvent = (
  event: Event,
  onFandomsChange: (fandoms: string[]) => void,
): void => handleListingFacetNavigateEvent(event, onFandomsChange);

export const handleListingCharacterNavigateEvent = (
  event: Event,
  onCharacterNavigate: (characterId: string, fandomId?: string) => void,
): void => {
  const customEvent = event as CustomEvent<ListingCharacterNavigateDetail>;
  const characterId = customEvent.detail?.characterId;
  const preventNavigation = customEvent.detail?.preventNavigation;

  if (!characterId || !preventNavigation) {
    return;
  }

  preventNavigation();
  customEvent.detail.handled = true;
  onCharacterNavigate(characterId, customEvent.detail.fandomId);
};

export const handleListingCatalogNavigateEvent = (
  event: Event,
  onCatalogNavigate: (catalogHref: string, preventNavigation: () => void) => void,
): void => {
  const customEvent = event as CustomEvent<ListingCatalogNavigateDetail>;
  const catalogHref = customEvent.detail?.catalogHref;
  const preventNavigation = customEvent.detail?.preventNavigation;
  if (!catalogHref || !preventNavigation) {
    return;
  }
  customEvent.detail.handled = true;
  onCatalogNavigate(catalogHref, preventNavigation);
};

export const handleListingFandomHrefNavigateEvent = (
  event: Event,
  onFandomHrefNavigate: (
    href: string,
    fandomId: string | undefined,
    preventNavigation: () => void,
  ) => void,
): void => {
  const customEvent = event as CustomEvent<ListingFandomHrefNavigateDetail>;
  const href = customEvent.detail?.href;
  const preventNavigation = customEvent.detail?.preventNavigation;
  if (!href || !preventNavigation) {
    return;
  }
  customEvent.detail.handled = true;
  onFandomHrefNavigate(href, customEvent.detail.fandomId, preventNavigation);
};

export const dispatchListingFandomHrefNavigate = (
  href: string,
  fandomId: string | undefined,
  preventDefault: () => void,
): void => {
  preventDefault();

  const detail: ListingFandomHrefNavigateDetail = {
    href,
    fandomId,
    preventNavigation: () => {},
    handled: false,
  };
  window.dispatchEvent(
    new CustomEvent<ListingFandomHrefNavigateDetail>(LISTING_FANDOM_HREF_NAVIGATE_EVENT, {
      detail,
    }),
  );

  if (detail.handled) {
    return;
  }

  window.location.assign(href);
};
